import React, { useRef, useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'next-i18next'
import { useDispatch } from 'react-redux'
import Image from 'next/image'
import { getMediaDataAction, uploadMediaAction } from 'store/actions/media.action'
import { notifyToast } from 'utils/responseUtils'
import { ProgressModal, MediaTaggingModal } from 'components/Modals'
import { useMediaQuery } from 'react-responsive'
import { downloadFile } from 'services/file.service'
import { cn } from '@/lib/utils'
import { Dialog, DialogClose } from '@components/ui/dialog'
import { Button } from '@components/ui/button'
import { X } from 'lucide-react' // Import the cross icon from lucide-react
import { OutsideClickHandler } from '@/lib/OutsideClickHandler'

const DragDropModal = ({ afterUploadCompleted = () => {}, onClose }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation('common')
  const isMobile = useMediaQuery({ maxWidth: 800 })

  const [dragActive, setDragActive] = useState(false)
  const [pastedUrl, setPastedUrl] = useState('')
  const [uploadProgress, setUploadProgress] = useState(null)
  const [isPostUpload, setPostUpload] = useState(false)

  const inputRef = useRef(null)
  const modalRef = useRef(null) // Ref for the modal content to detect outside clicks

  const handleFile = useCallback(
    async (files) => {
      let filesList = []
      const filesLength = files.length
      let errorsLength = 0
      setUploadProgress(true)

      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        const formData = new FormData()
        formData.append('media', file)
        dispatch(
          uploadMediaAction(
            formData,
            false,
            async (url, id) => {
              filesList.push({ url, id, tags: [] })
              processingHandler(filesList, errorsLength, filesLength)
            },
            () => {
              errorsLength += 1
              processingHandler(filesList, errorsLength, filesLength)
            }
          )
        )
      }
      dispatch(getMediaDataAction())
    },
    [dispatch, afterUploadCompleted]
  )

  const handlePastedFileUpload = useCallback(async () => {
    if (pastedUrl) {
      const file = await downloadFile(pastedUrl)
      handleFile([file])
    } else {
      dispatch(notifyToast('error', 'Please provide a valid URL'))
    }
  }, [pastedUrl, handleFile, dispatch])

  const processingHandler = useCallback(
    (filesList, errorsLength, filesLength) => {
      const percentage = (filesList.length + errorsLength) / filesLength
      setUploadProgress(percentage)
      if (percentage === 1 && filesList.length) {
        setPostUpload(JSON.parse(JSON.stringify(filesList)))
        afterUploadCompleted()
      }
      if (filesList.length + errorsLength === filesLength) {
        setUploadProgress(null)
      }
    },
    [afterUploadCompleted]
  )

  const handleDrag = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      setDragActive(false)
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        handleFile(e.dataTransfer.files)
      }
      e.target.value = ''
    },
    [handleFile]
  )

  const handleChange = useCallback(
    (e) => {
      e.preventDefault()
      if (e.target.files && e.target.files[0]) {
        handleFile(e.target.files)
      }
      e.target.value = ''
    },
    [handleFile]
  )

  const onButtonClick = useCallback(() => {
    inputRef.current.click()
  }, [])

  // Outside click handler
  const handleOutsideClick = useCallback(
    (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose()
      }
    },
    [onClose]
  )

  useEffect(() => {
    if (isMobile && inputRef.current) {
      inputRef.current.click()
    }
  }, [isMobile])

  useEffect(() => {
    // Add event listener for outside clicks
    document.addEventListener('mousedown', handleOutsideClick)
    return () => {
      // Cleanup event listener on unmount
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [handleOutsideClick])

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <div className={cn('fixed inset-0 bg-black/80 z-[999] flex items-center justify-center')}>
        <OutsideClickHandler onOutsideClick={onClose}>
          <div
            className={cn(
              'w-full min-w-[350px] min-h-[200px] md:w-[600px] h-[150px] md:h-[370px] p-8 bg-white rounded-lg shadow-xl',
              'max-w-none flex items-center justify-center relative'
            )}
          >
            {isPostUpload ? (
              <MediaTaggingModal onClose={onClose} files={isPostUpload} setFiles={setPostUpload} />
            ) : uploadProgress !== null ? (
              <ProgressModal title={t('common.media-progress')} progress={`${uploadProgress * 100}%`} />
            ) : (
              <div className="w-full  flex flex-col gap-6">
                {/* Upload Section */}
                <div className=" grid-cols-1 md:grid-cols-2 gap-4 md:h-[250px] hidden md:grid">
                  <form
                    className="relative flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 hover:bg-gray-100 transition-all duration-200 ease-in-out"
                    onDragEnter={handleDrag}
                    onDragOver={handleDrag}
                    onDragLeave={handleDrag}
                    onDrop={handleDrop}
                    onSubmit={(e) => e.preventDefault()}
                  >
                    <input
                      ref={inputRef}
                      type="file"
                      id="input-file-upload"
                      multiple
                      onChange={handleChange}
                      accept="image/*,video/*,audio/*"
                      className="hidden"
                    />
                    <label
                      htmlFor="input-file-upload"
                      className="flex flex-col items-center justify-center w-full h-full cursor-pointer"
                    >
                      <Image
                        src="/assets/common/upload.svg"
                        width={115}
                        height={115}
                        alt="upload"
                        className="mb-3 opacity-80"
                      />
                      <span className="text-sm font-bold text-gray-700">{t('common.drag-drop')}</span>
                    </label>
                    {dragActive && (
                      <div className="absolute inset-0 bg-blue-100/50 border-2 border-blue-500 rounded-xl" />
                    )}
                  </form>

                  <div
                    className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 hover:bg-gray-100 transition-all duration-200 ease-in-out cursor-pointer"
                    onClick={onButtonClick}
                  >
                    <Image
                      src="/assets/common/browse.svg"
                      width={115}
                      height={115}
                      alt="browse"
                      className="mb-3 opacity-80"
                    />
                    <span className="text-sm font-bold text-gray-700">{t('common.upload-from')}</span>
                  </div>
                </div>

                {/* URL Input Section */}
                <div className="flex items-center gap-4  px-4 py-2 bg-[#f2f2f2] rounded-md shadow-sm">
                  <Image
                    src="/assets/common/link-chain-icon.svg"
                    width={20}
                    height={20}
                    alt="link"
                    className="md:w-5 md:h-5 w-4 h-4 ml-3 md:ml-0 text-gray-500"
                  />
                  <input
                    placeholder="Paste URL"
                    type="text"
                    value={pastedUrl}
                    onChange={(e) => setPastedUrl(e.target.value)}
                    onPaste={(e) => setPastedUrl(e.target.value)}
                    className="flex-1 bg-transparent border-none outline-none text-sm text-gray-800 placeholder:text-gray-400 font-medium"
                  />
                  <Button
                    onClick={handlePastedFileUpload} // Fixed to not pass pastedUrl directly
                    size="sm"
                    className="bg-black hover:bg-gray-800 text-white font-medium rounded-md"
                  >
                    Upload
                  </Button>
                </div>

                {/* Mobile Upload Button */}
                <div className="sm:hidden">
                  <Button
                    onClick={onButtonClick}
                    variant="outline"
                    className="w-full text-gray-700 border-gray-300 hover:bg-gray-100"
                  >
                    Browse Files
                  </Button>
                </div>
              </div>
            )}
            {/* Cross Icon Button */}
            <DialogClose
              onClick={onClose}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 transition-colors p-1 rounded-full hover:bg-gray-100"
            >
              <X className="h-5 w-5" /> {/* Cross icon from lucide-react */}
              <span className="sr-only">Close</span> {/* Screen reader text */}
            </DialogClose>
          </div>
        </OutsideClickHandler>
      </div>
    </Dialog>
  )
}

export default DragDropModal
